import {
  CloseCircleOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Flex, Form, Input, Space, Spin } from 'antd';
import { useContext, type FC } from 'react';

import { MultipleSelect } from '@app/components';
import { PREFIX } from '@app/constants';
import { SendoutsContext } from '@app/providers/SendoutsProvider';

import { useFilterForm } from '@pages/Sendouts/SendoutsList/hooks/useFilterForm';
import { useFilterFormEffects } from '@pages/Sendouts/SendoutsList/hooks/useFilterFormEffects';

import type { SendoutsFilterFormProps } from '@pages/Sendouts/SendoutsList/types';

import '@pages/Sendouts/SendoutsList/styles/SendoutsFilters.desktop.scss';
import '@pages/Sendouts/SendoutsList/styles/SendoutsFilters.mobile.scss';

export const SendoutsFilterForm: FC<SendoutsFilterFormProps> = ({
  onChange,
}) => {
  const { rules, reset, prepareSubmitValues } = useFilterForm();
  const { isLoading } = useFilterFormEffects();

  const {
    searchTerm,
    statusOptions,
    channelOptions,
    integrationOptions,
    selectedChannelOptions,
    selectedIntegrationOptions,
    selectedStatusOptions,
    setSearchTerm,
    setSelectedChannelOptions,
    setSelectedIntegrationOptions,
    setSelectedStatusOptions,
  } = useContext(SendoutsContext);

  return (
    <Spin spinning={isLoading}>
      <Form
        className={`${PREFIX}sendouts-filter-form`}
        initialValues={{
          SearchTerm: searchTerm || '',
          ChannelIds: selectedChannelOptions.map(({ value }) => value),
          IntegrationIds: selectedIntegrationOptions.map(({ value }) => value),
          StatusIds: selectedStatusOptions.map(({ value }) => value),
        }}
        onFinish={(values) => {
          onChange(
            prepareSubmitValues(
              values,
              selectedStatusOptions,
              selectedChannelOptions,
              selectedIntegrationOptions
            )
          );
        }}
      >
        <Flex vertical={true}>
          <Form.Item
            name="SearchTerm"
            rules={rules.term}
            className="filter-term"
          >
            <Input
              placeholder="Введите название или часть текста рассылки"
              addonBefore={<SearchOutlined />}
              value={searchTerm}
              onChange={({ target }) => setSearchTerm(target.value || '')}
              data-testid="search-sendouts-input"
            />
          </Form.Item>
          <Space wrap={true} className="selectors-space">
            <Form.Item name="StatusIds" className="filter-statuses">
              <MultipleSelect
                className="multiple-select-filter status-select"
                placeholder="Все статусы"
                defaultSelected={true}
                allOptions={statusOptions}
                selectedOption={selectedStatusOptions}
                setSelectedOptions={setSelectedStatusOptions}
                width={100}
                dataTestId="all-status-input"
                dropdownDataTestId="status-dropdown"
                optionDataTestId="status-item"
              />
            </Form.Item>
            <Form.Item name="ChannelIds" className="filter-channel">
              <MultipleSelect
                className="multiple-select-filter channel-select"
                placeholder="Все каналы"
                defaultSelected={true}
                allOptions={channelOptions}
                selectedOption={selectedChannelOptions}
                setSelectedOptions={setSelectedChannelOptions}
                width={100}
                dataTestId="all-channels-input"
                dropdownDataTestId="channel-dropdown"
                optionDataTestId="channel-item"
              />
            </Form.Item>
            <Form.Item name="IntegrationIds" className="filter-integrations ">
              <MultipleSelect
                className="multiple-select-filter integration-select"
                placeholder="Все интеграции"
                defaultSelected={true}
                allOptions={integrationOptions}
                selectedOption={selectedIntegrationOptions}
                setSelectedOptions={setSelectedIntegrationOptions}
                width={100}
                dataTestId="all-integrations-input"
                dropdownDataTestId="integration-dropdown"
                optionDataTestId="integration-item"
              />
            </Form.Item>
          </Space>
          <Space className="action-buttons-container">
            <Button
              htmlType="reset"
              onClick={() => {
                setSelectedChannelOptions([]);
                setSelectedIntegrationOptions([]);
                setSelectedStatusOptions([]);
                reset(onChange);
              }}
              data-testid="reset-filters"
            >
              <CloseCircleOutlined />
              Сброс
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              data-testid="search-by-filters"
            >
              <FilterOutlined />
              Поиск
            </Button>
          </Space>
        </Flex>
      </Form>
    </Spin>
  );
};
